import { React, useState, createContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { USER_ROLES, EMAILS_BLOQUEADOS, MCP_LOGIN_PATH } from '../helpers/constants';
import loginApiService from '../services/http/loginApiService';
import otrosApiService from '../services/http/otrosApiService';
import { getItem, setItem, removeItem, clear, STORAGE_KEYS } from '../services/storage';
import config from '../config';

// Comprueba si el email logueado sigue en la whitelist (misma lógica que Login.js).
// Si no hay whitelist configurada, no bloquea a nadie.
// NOTA DE SEGURIDAD: es un control COSMÉTICO (mejora la UX cerrando sesiones ya
// revocadas), NO una barrera de seguridad — la lista está en el cliente y es
// evadible. La autorización real la impone el backend en cada petición.
export const estaEnWhitelist = (email) => {
  const normalizado = (email || '').trim().toLowerCase();
  // La denylist tiene prioridad: un email bloqueado nunca pasa, esté o no en la whitelist.
  if (EMAILS_BLOQUEADOS.includes(normalizado)) return false;
  const emails = (config.REACT_APP_WHITELIST || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  if (emails.length === 0) return true;
  return !!normalizado && emails.includes(normalizado);
};

export const AuthContext = createContext('hola');

const initialState = null;
const initialStateOU = { centro: '', id: '', idCedesca: -4, idCntec: -4 };

export const ContextProvider = (props) => {
  // Seteamos variables de estado necesarias
  const [user, setUser] = useState(initialState);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userOU, setUserOU] = useState(initialStateOU);
  const [loadingUserOU, setLoadingUserOU] = useState(true);

  /*** FUNCIONES ***/

  //Obtenemos la información de la unidad organizativa del usuario
  useEffect(() => {
    const fetchData = async () => {
      setLoadingUserOU(true);

      try {
        const dataOrgUnit = await otrosApiService.getOrgUnit();
        setUserOU(dataOrgUnit.data);
      } catch (error) {
        setUserOU(initialStateOU);

        // En este caso sí mandamos al usuario fuera ya que es un fallo de token expirado o fallo del token general.
        sessionStorage.setItem('apiError', error);
        removeItem(STORAGE_KEYS.USER);
        window.location.href = '/';
      } finally {
        setLoadingUserOU(false);
      }
    };

    // /mcp-login gestiona su propia sesión caducada: aquí no deben actuar las
    // redirecciones globales a `/` (fallo de getOrgUnit ni guard de whitelist,
    // que además lee `profileObj`, suplantable en el navegador).
    if (window.location.pathname === MCP_LOGIN_PATH) {
      setLoadingUserOU(false);
      return;
    }

    const userSession = getItem(STORAGE_KEYS.USER);

    if (userSession !== null) {
      // Guard: si el email de la sesión ya no está en la whitelist, cerramos sesión.
      // Revoca el acceso de usuarios con sesión abierta tras quitarlos de la whitelist,
      // sin esperar a que caduque el token. Al volver al login quedan bloqueados.
      if (!estaEnWhitelist(userSession?.profileObj?.email)) {
        removeItem(STORAGE_KEYS.USER);
        sessionStorage.clear();
        setLoadingUserOU(false);
        setLoadingUser(false);
        window.location.href = '/';
        return;
      }
      fetchData();
    } else {
      setLoadingUserOU(false);
      setLoadingUser(false);
    }
  }, [user]);

  // Función para hacer el login con Google. `silent` (solo /mcp-login) evita el
  // toast global de error: esa pantalla muestra su propio aviso.
  const loginGoogle = async ({ tokenId, profileObj }, { silent = false } = {}) => {
    setLoadingUser(true);

    try {
      const data = silent
        ? await loginApiService.auth({ tokenId, profileObj }, { silent: true })
        : await loginApiService.auth({ tokenId, profileObj });
      const tokenJWT = data?.data?.token;

      // Almacenamos la información del usuario
      const newUserInformation = { tokenId: tokenJWT, profileObj, impersonating: false };
      setUser(newUserInformation);
      setItem(STORAGE_KEYS.USER, newUserInformation);
      return true;
    } catch (error) {
      return false;
    } finally {
      setLoadingUser(false);
    }
  };

  // Función para obtener la OU
  const getUserOU = async () => {
    try {
      const dataOrgUnit = await otrosApiService.getOrgUnit();
      setUserOU(dataOrgUnit.data);
    } catch (error) {
      setUserOU(initialStateOU);
    }
  };

  // Función para cerrar la sesión
  const logoutGoogle = () => {
    setUser(null);
    clear();
    sessionStorage.clear();
  };

  // Función que devuelve el rol del usuario logueado.
  const getUserRol = () => {
    const isSuperadmin = userOU.idCedesca === 0 && userOU.idCntec === 0;
    if (isSuperadmin) return USER_ROLES.SUPERADMIN;
    const isSede = userOU.idCedesca === -1 && userOU.idCntec === -1;
    if (isSede) return USER_ROLES.SEDE;
    const isOrientacion = userOU.idCedesca === -2 && userOU.idCntec === -2;
    if (isOrientacion) return USER_ROLES.ORIENTACION;
    const isSecretaria = userOU.idCedesca === -3 && userOU.idCntec === -3;
    if (isSecretaria) return USER_ROLES.SECRETARIA;
    // Delegación: basta con pertenecer a UNA empresa (id de centro > 0). Las
    // delegaciones de una sola sede traen la otra en null (p.ej. CAMP JOLIU:
    // idCedesca 13, idCntec null); con `&&` quedaban sin rol y sin acceso.
    const isDelegacion = userOU.idCedesca > 0 || userOU.idCntec > 0;
    if (isDelegacion) return USER_ROLES.DELEGACION;
  };

  return (
    <>
      {userOU.idCedesca !== -4 ? (
        <AuthContext.Provider
          value={{
            user,
            loginGoogle,
            logoutGoogle,
            userOU,
            loadingUserOU,
            loadingUser,
            getUserOU,
            getUserRol,
          }}
        >
          {props.children}
        </AuthContext.Provider>
      ) : (
        <AuthContext.Provider
          value={{
            user,
            userOU,
            loadingUserOU,
            loadingUser,
            loginGoogle,
            logoutGoogle,
            getUserOU,
            getUserRol,
          }}
        >
          {props.children}
        </AuthContext.Provider>
      )}
    </>
  );
};

ContextProvider.propTypes = {
  children: PropTypes.node,
};
